import { DataBaseNodes, PatientWearables } from '@lib/firebase';
import { useSiteStore } from '@stores';
import { WearableDataObject, WearableScopes, WithingsTokens } from '@type';

import { CommunicationKeys, Preferences } from '../types';
import { BaseGateway } from './base.gateway';

export class WearableGateway extends BaseGateway {
  submitWearableHealthData = async (wearableObject: WearableDataObject) => {
    try {
      const { getSiteByProjectId } = useSiteStore.getState();
      const siteData = getSiteByProjectId(this.config.projectId as string);

      const payload = {
        encrypted: false,
        message: wearableObject,
      };

      return await this.sendRequest(
        siteData.siteUuid,
        payload,
        DataBaseNodes.Responses
      );
    } catch (error) {
      throw new Error(
        `Error on submitting wearables health data: ${JSON.stringify(error)}`
      );
    }
  };

  getPatientWearables = async () => {
    return await this.sourceService.readDataOnce<PatientWearables>(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.Wearables}`
    );
  };

  saveWithingsTokens = async (
    withingsTokens: WithingsTokens,
    scopes: WearableScopes
  ) => {
    try {
      const { getSiteByProjectId } = useSiteStore.getState();
      const siteData = getSiteByProjectId(this.config.projectId as string);
      const firebasePatientId = this.config.patientSourceUUID;

      const payload = {
        preference: Preferences.Wearables.SaveWithingsTokens,
        firebasePatientId,
        ...withingsTokens,
        scopes,
      };

      return await this.dataExchange<{
        message: string;
        success: boolean;
      }>({
        siteUuid: siteData.siteUuid,
        payload: payload,
        query: DataBaseNodes.Request,
        successKey: CommunicationKeys.Wearables.WithingsAuthSaveDone,
        failureKey: [
          CommunicationKeys.Wearables.WithingsAuthSaveFailed,
          CommunicationKeys.Wearables.WithingsIdDuplicateInDB,
        ],
        pushWithKey: true,
      });
    } catch (error) {
      throw new Error(
        `Error on saving withings tokens : ${JSON.stringify(error)}`
      );
    }
  };

  getWithingsTokens = async () => {
    try {
      const { getSiteByProjectId } = useSiteStore.getState();
      const siteData = getSiteByProjectId(this.config.projectId as string);
      const firebasePatientId = this.config.patientSourceUUID;

      const payload = {
        preference: Preferences.Wearables.GetWearableTokens,
        firebasePatientId,
      };

      return await this.dataExchange<{
        withingsTokens: WithingsTokens;
        scopes: WearableScopes;
      }>({
        siteUuid: siteData.siteUuid,
        payload: payload,
        query: DataBaseNodes.Request,
        successKey: CommunicationKeys.Wearables.GetWithingsKeysSuccess,
        failureKey: CommunicationKeys.Wearables.GetWithingsKeysFailed,
        pushWithKey: true,
      });
    } catch (error) {
      throw new Error(
        `Error on fetching withings tokens : ${JSON.stringify(error)}`
      );
    }
  };

  withingsRollback = async () => {
    try {
      const { getSiteByProjectId } = useSiteStore.getState();
      const siteData = getSiteByProjectId(this.config.projectId as string);
      const firebasePatientId = this.config.patientSourceUUID;

      const payload = {
        preference: Preferences.Wearables.WithingsRollBack,
        firebasePatientId,
      };

      return await this.dataExchange<{
        message: string;
        success: boolean;
      }>({
        siteUuid: siteData.siteUuid,
        payload: payload,
        query: DataBaseNodes.Request,
        successKey: CommunicationKeys.Wearables.WithingsRollbackSuccess,
        failureKey: CommunicationKeys.Wearables.WithingsRollbackFailed,
        pushWithKey: true,
      });
    } catch (error) {
      throw new Error(
        `Error on rolling back withings tokens : ${JSON.stringify(error)}`
      );
    }
  };
}
